// チャット（仕様書「事務所とのチャット」）。本文は Firestore の messages に直接書き（Security Rules で送り主と宛先を確かめる）、
// そのあと API に既読などの印を付けてもらう。本文はサーバーを通らない
import { Timestamp, collection, doc, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc, where, writeBatch } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { OFFICE_MESSAGE_TEMPLATES, RULES, newMessageId, type MessageDoc, type PhrasesDoc } from '@torapon/shared';
import { api } from './api';
import { fsdb } from './firebase';

export type ChatLine = MessageDoc & { id: string; at: Date | null };

export async function sendToDriver(me: { id: string; officeId: string }, driverId: string, text: string, o: { urgent: boolean; deliveryNo: string | null; kind?: 'text' | 'template'; id?: string }) {
  const id = o.id ?? newMessageId();
  const kind = o.kind ?? 'text';
  const body = {
    officeId: me.officeId, driverId, from: 'staff' as const, staffId: me.id, kind, text, deliveryNo: o.deliveryNo, urgent: o.urgent,
    createdAt: serverTimestamp(), expireAt: Timestamp.fromMillis(Date.now() + RULES.chatKeepDays * 86_400_000),
  };
  await setDoc(doc(fsdb(), 'messages', id), body);
  await api.post('/v1/office/messages', { id, driverId, kind, urgent: o.urgent, deliveryNo: o.deliveryNo });
  return id;
}

/** 一斉連絡：宛先ごとに 1 通ずつ Firestore に置いてから、まとめて印を付ける（Security Rules は 1 通ずつ確かめる） */
export async function broadcast(me: { id: string; officeId: string }, driverIds: string[], text: string, urgent: boolean) {
  const expireAt = Timestamp.fromMillis(Date.now() + RULES.chatKeepDays * 86_400_000);
  const items = driverIds.map((driverId) => ({ id: newMessageId(), driverId }));
  const body = (x: { driverId: string }) => ({ officeId: me.officeId, driverId: x.driverId, from: 'staff', staffId: me.id, kind: 'broadcast', text, deliveryNo: null, urgent, createdAt: serverTimestamp(), expireAt });
  // 8 人分ずつまとめて書く（Security Rules が 1 回に読める数の内に収める）。まとめて書けなければ 1 人ずつ書き直し、
  // 書けない人がいても（名簿の不整合など）ほかの人には届ける
  const okIds = new Set<string>();
  await Promise.all(Array.from({ length: Math.ceil(items.length / 8) }, (_, k) => items.slice(k * 8, k * 8 + 8)).map(async (part) => {
    const b = writeBatch(fsdb());
    for (const x of part) b.set(doc(fsdb(), 'messages', x.id), body(x));
    try { await b.commit(); part.forEach((x) => okIds.add(x.id)); }
    catch { await Promise.all(part.map((x) => setDoc(doc(fsdb(), 'messages', x.id), body(x)).then(() => okIds.add(x.id), () => {}))); }
  }));
  const done = items.map((x) => ({ status: okIds.has(x.id) ? 'fulfilled' : 'rejected' }));
  const ok = items.filter((_, i) => done[i]!.status === 'fulfilled');
  if (!ok.length) throw new Error('送れませんでした');
  for (let i = 0; i < ok.length; i += 200) await api.post('/v1/office/broadcasts', { urgent, items: ok.slice(i, i + 200) });
  return { sent: ok.length, failed: items.length - ok.length };
}

/** ドライバーとのやり取り（新しい順に 100 件まで）を見続ける */
export function watchThread(officeId: string, driverId: string, on: (lines: ChatLine[]) => void, onErr: () => void) {
  const q = query(collection(fsdb(), 'messages'), where('officeId', '==', officeId), where('driverId', '==', driverId), orderBy('createdAt', 'desc'), limit(100));
  return onSnapshot(q, (snap) => {
    on(snap.docs.map((d) => {
      const x = d.data({ serverTimestamps: 'estimate' }) as MessageDoc;
      const ts = x.createdAt as Timestamp | null;
      return { ...x, id: d.id, at: ts ? ts.toDate() : null };
    }).reverse());
  }, onErr);
}

/** 事務所の定型文（まだ直していなければ初めの一覧）。Firestore の officePhrases を見続ける */
export function useOfficePhrases(officeId: string) {
  const [items, setItems] = useState<string[]>([...OFFICE_MESSAGE_TEMPLATES]);
  useEffect(() => onSnapshot(doc(fsdb(), 'officePhrases', officeId), (snap) => { const d = snap.data() as PhrasesDoc | undefined; if (d?.items) setItems(d.items); }, () => {}), [officeId]);
  return items;
}
export const saveOfficePhrases = (officeId: string, items: string[]) => setDoc(doc(fsdb(), 'officePhrases', officeId), { officeId, items } satisfies PhrasesDoc);

/** チャット専用の音（警告の音とは別） */
export function chatChime() {
  try {
    const ac = new AudioContext();
    let t = ac.currentTime;
    for (const f of [660, 990]) {
      const o = ac.createOscillator(), g = ac.createGain();
      o.type = 'triangle'; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.18, t + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
      o.connect(g).connect(ac.destination); o.start(t); o.stop(t + 0.27); t += 0.16;
    }
  } catch { /* 音が出せない環境では鳴らさない */ }
}
